import { FC, useCallback, FocusEventHandler } from 'react';
import { TextField } from '@mui/material';
import { fieldToTextField, TextFieldProps } from 'formik-mui';

export type IdecoTextFieldProps = {
  onBlurCallback?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
} & TextFieldProps;

/**
 * @summary Кастомный TextField component, который убирает пробелы в начале и в конце строки.
 * @description Компонент заменяет TextField из formik-mui. При переопределении метода onBlur,
 * функционал работать не будет. Для этого предусмотрен дополнительный метод onBlurCallback.
 * @see https://stackworx.github.io/formik-mui/docs/api/material-ui
 */
export const IdecoTextField: FC<IdecoTextFieldProps> = ({ onBlurCallback, ...props }) => {
  const {
    form: { setFieldValue },
    field: { name, onBlur: fieldOnBlur },
    onBlur: defaultBlurHandler,
  } = props;

  const customBlurHandler: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (event) => {
      fieldOnBlur(event ?? name);
      const value = event?.target.value;

      if (typeof value === 'string') {
        setFieldValue(name, value.trim());
      }

      if (onBlurCallback) {
        onBlurCallback(event);
      }
    },
    [name, fieldOnBlur, setFieldValue]
  );

  const blurHandler: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = useCallback(defaultBlurHandler ?? customBlurHandler, [defaultBlurHandler, customBlurHandler]);

  return (
    <TextField
      size="small"
      variant="outlined"
      autoComplete="off"
      {...fieldToTextField(props)}
      onBlur={blurHandler}
    />
  );
};
